/**
 * Data client for the dashboard.
 *
 * ┌────────────────────────────────────────────────────────────────────────┐
 * │  This is the single boundary between the UI and the data.                │
 * │  Today it computes everything in-browser from local mock data via        │
 * │  aggregate.ts — there is NO server, NO Worker, NO build-time backend.    │
 * │                                                                          │
 * │  When the real Portal API (agw-config-api) is wired up, swap the mock    │
 * │  functions in lib/data.ts for browser fetch() calls that use the         │
 * │  engineer's own Cloudflare Access session. The UI, hooks, and components │
 * │  do not change — they only ever call this client.                        │
 * └────────────────────────────────────────────────────────────────────────┘
 */

import type {
  MatrixApiResponse,
  ServerDetail,
  AccountDetail,
  SearchResult,
} from '../types'
import {
  buildMatrixResponse,
  buildKpiSummary,
  buildServerDetail,
  buildAccountDetail,
  buildMatrix,
  summarizeServer,
  summarizeAccount,
} from './aggregate'

export const client = {
  /** Full account×server matrix + KPI summary. */
  async matrix(): Promise<MatrixApiResponse> {
    const [matrix, kpis] = await Promise.all([
      buildMatrixResponse(),
      buildKpiSummary(),
    ])
    return { ...matrix, kpis }
  },

  /** Per-server drilldown (customers affected by this server). */
  async server(id: string): Promise<ServerDetail> {
    const detail = await buildServerDetail(id)
    if (!detail) throw new Error('Server not found')
    return detail
  },

  /** Per-account drilldown (servers this account connects to). */
  async account(id: string): Promise<AccountDetail> {
    const detail = await buildAccountDetail(id)
    if (!detail) throw new Error('Account not found')
    return detail
  },

  /** Search accounts + servers by name / id / provider. */
  async search(q: string): Promise<{ results: SearchResult[] }> {
    const query = q.trim().toLowerCase()
    if (!query) return { results: [] }

    const { accounts, servers, cells } = await buildMatrix()
    const results: SearchResult[] = []

    for (const s of servers) {
      if (
        s.server_name.toLowerCase().includes(query) ||
        s.provider.toLowerCase().includes(query) ||
        s.server_id.toLowerCase().includes(query)
      ) {
        const sum = summarizeServer(s, cells)
        results.push({
          type: 'server',
          id: s.server_id,
          label: s.server_name,
          sublabel: `${s.provider} · ${sum.total_connected_accounts} accounts`,
          health: sum.health,
        })
      }
    }

    for (const a of accounts) {
      if (
        a.account_name.toLowerCase().includes(query) ||
        a.account_id.toLowerCase().includes(query)
      ) {
        const sum = summarizeAccount(a, cells)
        results.push({
          type: 'account',
          id: a.account_id,
          label: a.account_name,
          sublabel: `${a.account_id.slice(0, 12)}… · ${sum.total_connected_servers} servers`,
          health: sum.health,
        })
      }
    }

    return { results: results.slice(0, 20) }
  },
}
